import React, { useMemo } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { ListItemSeparator, ListOption } from '@/components/list-item';
import {
  ListSection,
  RecyclerList,
  Props as RecyclerListProps,
} from '@/components/recycler-list';
import { PaymentHistoryListFieldsFragment } from '@/types';
import { Spacing } from '@usepepper/sprout-kit';

const ESTIMATED_LIST_ITEM_HEIGHT = 70;

export type PaymentHistoryListOption = ListOption<{
  payment: PaymentHistoryListFieldsFragment;
}>;

export type Props = {
  listSeparatorSpacing?: Spacing;
  paymentBatches: PaymentHistoryListFieldsFragment[];
  renderListItem: (
    payment: PaymentHistoryListFieldsFragment,
  ) => React.ReactElement | null;
  selectedPaymentUUID?: string | null;
} & Omit<
  RecyclerListProps<PaymentHistoryListOption>,
  'estimatedItemSize' | 'sections'
>;

export const PaymentHistoryList: React.FC<Props> = ({
  listSeparatorSpacing,
  paymentBatches,
  renderListItem,
  selectedPaymentUUID,
  ...rest
}) => {
  const [styles, theme] = useStyles();

  const sections = useMemo<ListSection<PaymentHistoryListOption>[]>(() => {
    return [
      {
        data: paymentBatches.map<PaymentHistoryListOption>((pb) => ({
          metadata: {
            payment: pb,
          },
          title: pb.uuid,
          value: pb.uuid,
        })),
        key: null,
      },
    ];
  }, [paymentBatches]);

  return (
    <RecyclerList
      estimatedItemSize={ESTIMATED_LIST_ITEM_HEIGHT}
      extraData={{ selectedPaymentUUID }}
      ItemSeparatorComponent={() => (
        <ListItemSeparator
          style={[
            styles.listItemSeparator,
            ...(listSeparatorSpacing
              ? [{ marginHorizontal: theme.sk.spacing[listSeparatorSpacing] }]
              : []),
          ]}
        />
      )}
      renderItem={({ item }) => {
        const payment = item.metadata?.payment;
        if (!payment) {
          return null;
        }
        return renderListItem(payment);
      }}
      sections={sections}
      testID="payment-history-list"
      {...rest}
    />
  );
};

const useStyles = createStyles((theme) => ({
  listItemSeparator: {
    backgroundColor: theme.sk.color.divider.default,
    marginHorizontal: theme.sk.spacing.spaceMedium,
  },
}));
